import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Loader2, Heart, Plane, MapPin, ArrowLeft, Trash2, 
  Share2, Calendar, Armchair, MessageCircle, Send
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

export default function PostDetail() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isLiking, setIsLiking] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchPost();
    fetchComments();
  }, [postId]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/posts/${postId}`, {
        withCredentials: true
      });
      setPost(response.data);
      setIsLiked(response.data.is_liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Post not found');
      navigate('/feed');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await axios.get(`${API}/posts/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) {
      toast.error('Please sign in to like posts');
      return;
    }

    if (isLiking) return;
    setIsLiking(true);

    try {
      const response = await axios.post(
        `${API}/posts/${postId}/like`,
        {},
        { withCredentials: true }
      );
      
      setIsLiked(response.data.liked);
      setLikesCount(response.data.likes_count);
    } catch (error) {
      console.error('Like error:', error);
      toast.error('Failed to update like');
    } finally {
      setIsLiking(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/posts/${postId}`, { withCredentials: true });
      toast.success('Post deleted successfully');
      navigate('/feed');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete post');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: 'Window View on Ventana',
        text: post.caption || 'Check out this amazing window view!',
        url: window.location.href
      });
    } catch (error) {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please sign in to comment');
      return;
    }

    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const response = await axios.post(
        `${API}/posts/${postId}/comments`,
        { content: newComment },
        { withCredentials: true }
      );
      
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment added');
    } catch (error) {
      console.error('Comment error:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await axios.delete(`${API}/comments/${commentId}`, { withCredentials: true });
      setComments(comments.filter(c => c.comment_id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      console.error('Delete comment error:', error);
      toast.error('Failed to delete comment');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="post-loading">
        <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!post) return null;

  const isOwner = user?.user_id === post.user_id;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="post-detail-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate(-1)}
        className="mb-6 text-gray-400 hover:text-white"
        data-testid="back-btn"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Image */}
        <div className="lg:col-span-3">
          <div className="rounded-2xl overflow-hidden glass-card">
            <img 
              src={post.image_url} 
              alt={post.caption || 'Window view'}
              className="w-full h-auto max-h-[70vh] object-contain bg-black"
              data-testid="post-image"
            />
          </div>
        </div>

        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Author */}
          <div className="glass-card rounded-2xl p-6">
            <Link 
              to={`/profile/${post.author?.user_id}`}
              className="flex items-center gap-3 mb-4 hover:opacity-80 transition-opacity"
              data-testid="author-link"
            >
              <Avatar className="w-12 h-12">
                <AvatarImage src={post.author?.picture} alt={post.author?.name} />
                <AvatarFallback className="bg-purple-900 text-white">
                  {post.author?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-semibold text-white">{post.author?.name}</p>
                <p className="text-sm text-gray-400">
                  {post.created_at && format(new Date(post.created_at), 'MMM d, yyyy')}
                </p>
              </div>
            </Link>

            {/* Caption */}
            {post.caption && (
              <p className="text-gray-200 mb-4">{post.caption}</p>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleLike}
                disabled={isLiking}
                className={`like-btn flex-1 justify-center ${isLiked ? 'liked' : ''}`}
                data-testid="like-btn"
              >
                <Heart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                <span>{likesCount}</span>
              </button>

              <Button 
                onClick={handleShare}
                variant="outline"
                className="btn-secondary"
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4" />
              </Button>

              {isOwner && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline"
                      className="btn-secondary text-red-400 hover:text-red-300"
                      data-testid="delete-btn"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0F0F16] border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Delete this post?</AlertDialogTitle>
                      <AlertDialogDescription className="text-gray-400">
                        This action cannot be undone. This will permanently delete your window view.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        disabled={isDeleting}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>

          {/* Flight Details */}
          {(post.airline || post.aircraft_type || post.departure || post.arrival || post.seat) && (
            <div className="glass-card rounded-2xl p-6">
              <h3 
                className="text-lg font-semibold text-white mb-4 flex items-center gap-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                <Plane className="w-5 h-5 text-purple-400" />
                Flight Details
              </h3>

              <div className="space-y-3">
                {post.airline && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Airline</span>
                    <span className="text-white">{post.airline}</span>
                  </div>
                )}
                {post.aircraft_type && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Aircraft</span>
                    <span className="text-white">{post.aircraft_type}</span>
                  </div>
                )}
                {post.departure && post.arrival && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400">Route</span>
                    <span className="text-white font-mono">{post.departure} → {post.arrival}</span>
                  </div>
                )}
                {post.seat && (
                  <div className="flex items-center justify-between">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Armchair className="w-4 h-4" />
                      Seat
                    </span>
                    <span className="text-white">{post.seat}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          {post.latitude && post.longitude && (
            <div className="glass-card rounded-2xl p-6">
              <h3 
                className="text-lg font-semibold text-white mb-4 flex items-center gap-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                <MapPin className="w-5 h-5 text-purple-400" />
                Location
              </h3>
              
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400">Coordinates</span>
                <span className="text-white font-mono text-sm">
                  {post.latitude.toFixed(4)}, {post.longitude.toFixed(4)}
                </span>
              </div>

              <Link 
                to="/globe"
                className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-purple-500/20 text-purple-400 hover:bg-purple-500/30 transition-colors"
              >
                View on Globe
              </Link>
            </div>
          )}

          {/* Comments Section */}
          <div className="glass-card rounded-2xl p-6">
            <h3 
              className="text-lg font-semibold text-white mb-4 flex items-center gap-2"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              <MessageCircle className="w-5 h-5 text-purple-400" />
              Comments ({comments.length})
            </h3>

            {/* Comment Form */}
            {isAuthenticated ? (
              <form onSubmit={handleSubmitComment} className="flex gap-2 mb-4">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="input-ventana flex-1"
                  data-testid="comment-input"
                />
                <Button
                  type="submit"
                  disabled={submittingComment || !newComment.trim()}
                  className="btn-primary-gradient rounded-lg px-4"
                  data-testid="submit-comment-btn"
                >
                  {submittingComment ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </form>
            ) : (
              <p className="text-sm text-gray-400 mb-4">
                <Link to="/" className="text-purple-400 hover:underline">Sign in</Link> to comment
              </p>
            )}

            {/* Comments List */}
            <div className="space-y-4 max-h-[300px] overflow-y-auto">
              {loadingComments ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : comments.length > 0 ? (
                comments.map((comment) => (
                  <div key={comment.comment_id} className="flex gap-3" data-testid={`comment-${comment.comment_id}`}>
                    <Link to={`/profile/${comment.author?.user_id}`}>
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={comment.author?.picture} />
                        <AvatarFallback className="bg-purple-900 text-white text-xs">
                          {comment.author?.name?.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                    </Link>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Link 
                          to={`/profile/${comment.author?.user_id}`}
                          className="text-sm font-medium text-white hover:underline"
                        >
                          {comment.author?.name}
                        </Link>
                        <span className="text-xs text-gray-500">
                          {comment.created_at && formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                        </span>
                      </div>
                      <p className="text-sm text-gray-300 mt-1">{comment.content}</p>
                    </div>
                    {user?.user_id === comment.user_id && (
                      <button
                        onClick={() => handleDeleteComment(comment.comment_id)}
                        className="text-gray-500 hover:text-red-400 transition-colors"
                        data-testid={`delete-comment-${comment.comment_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 py-4">No comments yet. Be the first!</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { PostDetail };
